import { type KeyboardEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Menu, MenuContent } from '@/components/molecules/Menu';

import CheckboxMenuRows from './CheckboxMenuRows';
import CheckboxMenuSearch from './CheckboxMenuSearch';
import CheckboxMenuToggle from './CheckboxMenuToggle';
import { handleMenuKey } from './checkboxMenuKeyboard';
import {
    type CheckboxMenuOption,
    type CheckboxMenuProps,
    filterSections,
} from './checkboxMenuModel';

import styles from './CheckboxMenu.module.scss';

export type { CheckboxMenuProps } from './checkboxMenuModel';

export default function CheckboxMenu<TValue extends string = string>(
    props: CheckboxMenuProps<TValue>
) {
    const {
        label,
        summary,
        hideLabel = false,
        options,
        selected,
        onChange,
        searchable = false,
        groupScope = 'visible',
        alignRight = false,
        labelledBy,
        disabled = false,
        className,
    } = props;
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const menuRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const selectedSet = useMemo(() => new Set<string>(selected), [selected]);
    const visibleSections = useMemo(() => filterSections(options, query), [options, query]);
    // An empty list with nothing typed is options still loading, not a failed search. The
    // region below stays mounted: one added together with its text is often not announced.
    const noMatches = query.trim() !== '' && visibleSections.length === 0;
    const groupOptions = useMemo(() => {
        const byGroupId = new Map<string, readonly CheckboxMenuOption<TValue>[]>();
        for (const section of options) {
            if (section.group) byGroupId.set(section.group.id, section.options);
        }
        return byGroupId;
    }, [options]);

    const setOpen = (next: boolean): void => {
        setIsOpen(next);
        if (!next) setQuery('');
    };

    const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        handleMenuKey(event, menuRef.current);
    };

    return (
        <Menu
            kind="list"
            isOpen={isOpen}
            disabled={disabled}
            onOpenChange={setOpen}
            className={classNames(styles.checkboxMenu, className, {
                [styles.field]: labelledBy !== undefined,
            })}>
            <CheckboxMenuToggle
                label={label}
                hideLabel={hideLabel}
                summary={summary}
                labelledBy={labelledBy}
                isOpen={isOpen}
                disabled={disabled}
            />
            {/* The popup is a menu unless told otherwise, and a menu may own only menu
                items, never a search field or checkboxes. With the label, the explicit
                role makes a named group behind a disclosure toggle (aria-haspopup false,
                aria-expanded kept). */}
            <MenuContent
                tag="fieldset"
                role="group"
                className={styles.popup}
                offset={4}
                // The search field takes focus however the menu opened. Without one, a
                // keyboard open lands on the first row and a click leaves focus on the toggle,
                // where moving it would ring a row the browser treats as focus-visible.
                initialFocus={searchable ? searchRef : undefined}
                align={alignRight ? 'end' : 'start'}
                aria-label={label}>
                <div ref={menuRef} className={styles.menuBody} onKeyDown={handleMenuKeyDown}>
                    {searchable ? (
                        <CheckboxMenuSearch
                            label={label}
                            query={query}
                            searchRef={searchRef}
                            onQueryChange={setQuery}
                        />
                    ) : null}
                    <div className={styles.options}>
                        {visibleSections.map((section, index) => (
                            <CheckboxMenuRows
                                key={section.group?.id ?? `section-${index}`}
                                section={section}
                                groupOptions={
                                    groupScope === 'all' && section.group
                                        ? groupOptions.get(section.group.id)
                                        : undefined
                                }
                                selected={selected}
                                selectedSet={selectedSet}
                                onChange={onChange}
                            />
                        ))}
                        <output className={styles.empty}>
                            {noMatches ? t('molecules.checkboxMenu.noResults') : ''}
                        </output>
                    </div>
                </div>
            </MenuContent>
        </Menu>
    );
}
